<script lang="ts" setup>
import AdaptiveDialog from '../AdaptiveDialog.vue'
import Button from 'primevue/button'
import SrDataTable from '../tables/SrDataTable.vue'
import TableCards from '../TableCards.vue'
import { ref, watch, useTemplateRef, onUnmounted, markRaw } from 'vue'
import { AutoTradeTriggerService } from '@/services/auto-trade-trigger-service'
import type { AutoTradeTrigger } from '@/services/auto-trade-trigger-service/types'
import type { TableColumn } from '@/components/alerts/types'
import { useMyToast } from '@/composable/useMyToast'
import { i18n } from '@/i18n'
import AutoTradeTriggerCreateModal from './AutoTradeTriggerCreateModal.vue'
import AutoTradeTriggerEditModal from './AutoTradeTriggerEditModal.vue'
import TriggerActions from './TriggerActions.vue'
import { isNull } from 'lodash'
import { getTradeUrl } from '@/utils/index'
import ActiveStatusPit from './ActiveStatusPit.vue'
import { useAutoRefresh } from '@/composable/useAutorefresh.ts'

const { addToast } = useMyToast()
const { t } = i18n.global

const visible = ref(false)
const triggers = ref<AutoTradeTrigger[]>([])
const loading = ref(false)
const deletingId = ref<number | null>(null)
const togglingId = ref<number | null>(null)
const resettingId = ref<number | null>(null)
// const togglingOneShotId = ref<number | null>(null)
const createModal = useTemplateRef<InstanceType<typeof AutoTradeTriggerCreateModal>>('createModal')
const editModal = useTemplateRef<InstanceType<typeof AutoTradeTriggerEditModal>>('editModal')

const toggle = () => {
  visible.value = !visible.value
}

defineExpose({
  toggle,
})

const { stopRefresh, startRefresh } = useAutoRefresh(() => {
  if (visible.value) {
    loadTriggers(false)
  }
}, 5000)

watch(visible, (v) => {
  if (v) {
    loadTriggers(true)
    startRefresh()
  } else {
    stopRefresh()
  }
})

onUnmounted(() => {
  stopRefresh()
})

const loadTriggers = async (setLoadingFlag = true) => {
  if (setLoadingFlag) {
    loading.value = true
  }
  try {
    const res = await AutoTradeTriggerService.fetchTriggers()
    triggers.value = res.data
  } catch (e: unknown) {
    console.error(e)
    // addToast({ severity: 'error', summary: t('error'), detail: t('fetchTriggersError'), life: 3000 })
  } finally {
    loading.value = false
  }
}

const deleteTrigger = async (id: number) => {
  deletingId.value = id
  try {
    await AutoTradeTriggerService.deleteTrigger(id)
    // addToast({ severity: 'success', summary: t('success'), detail: t('triggerDeleted'), life: 3000 })
    await loadTriggers(false)
  } catch (e: unknown) {
    console.error(e)
    addToast({ severity: 'error', summary: t('error'), detail: t('deleteTriggerError'), life: 3000 })
  } finally {
    deletingId.value = null
  }
}

const toggleActive = async (id: number) => {
  togglingId.value = id
  try {
    await AutoTradeTriggerService.toggleTrigger(id)
    await loadTriggers(false)
  } catch (e: unknown) {
    console.error(e)
    addToast({ severity: 'error', summary: t('error'), detail: t('toggleTriggerError'), life: 3000 })
  } finally {
    togglingId.value = null
  }
}

const resetFailed = async (id: number) => {
  resettingId.value = id
  try {
    await AutoTradeTriggerService.resetFailed(id)
    await loadTriggers(false)
  } catch (e: unknown) {
    console.error(e)
    addToast({ severity: 'error', summary: t('error'), detail: t('resetTriggerError'), life: 3000 })
  } finally {
    resettingId.value = null
  }
}

// const toggleOneShot = async (id: number) => {
//   togglingOneShotId.value = id
//   try {
//     await AutoTradeTriggerService.toggleOneShot(id)
//     await loadTriggers(false)
//   } catch (e: unknown) {
//     console.error(e)
//     addToast({ severity: 'error', summary: t('error'), detail: t('toggleOneShotError'), life: 3000 })
//   } finally {
//     togglingOneShotId.value = null
//   }
// }

const openCreateModal = () => {
  createModal.value?.toggle()
}

const editTrigger = (data: AutoTradeTrigger) => {
  editModal.value?.open(data)
}

const columns: TableColumn[] = [
  {
    field: 'ex_buy',
    header: 'modal.autoTradeTrigger.exBuy',
    class: 'text-center',
    inner: 'a',
    dynamic: true,
    innerPropsGenerator: (v) => {
      const data = v as AutoTradeTrigger
      return {
        href: getTradeUrl(data.ex_buy.name, data.asset.name, data.market_type.startsWith('swap'), data.quote_buy?.name),
        target: '_blank',
        class: 'pit success hover',
      }
    },
  },
  {
    field: 'ex_sell',
    header: 'modal.autoTradeTrigger.exSell',
    class: 'text-center',
    inner: 'a',
    dynamic: true,
    innerPropsGenerator: (v) => {
      const data = v as AutoTradeTrigger
      const sellAsset = data.sell_asset?.name ?? data.asset.name
      return {
        href: getTradeUrl(data.ex_sell.name, sellAsset, data.market_type.endsWith('swap'), data.quote_sell?.name),
        target: '_blank',
        class: 'pit danger hover',
      }
    },
  },
  {
    field: 'asset',
    header: 'asset',
    class: 'text-center',
    inner: 'a',
    dynamic: true,
    innerPropsGenerator: (v) => {
      const data = v as AutoTradeTrigger
      const amountSuffix = data.amount ? `/${data.amount}` : ''

      let mt = data.market_type
      if (mt == "swap-swap") {
        mt = "swap"
      } else if (mt == "spot") {
        mt = "spot"
      }

      return {
        target: '_blank',
        href: `/eye/${data.market_type}/${data.asset.name.toLowerCase()}/${data.ex_buy.name.toLowerCase()}/${data.ex_sell.name.toLowerCase()}${amountSuffix}`,
        class: 'pit uppercase hover',
      }
    },
  },
  {
    field: 'market_type',
    header: 'marketType',
    class: 'text-center w-45',
    inner: 'span',
    dynamic: true,
    innerPropsGenerator: () => ({ class: 'pit' }),
  },
  {
    field: 'entry_spread',
    header: 'entrySpread',
    class: 'text-center',
    inner: 'span',
    dynamic: true,
    innerPropsGenerator: () => ({ class: 'pit digit' }),
  },
  {
    field: 'exit_spread',
    header: 'exitSpread',
    class: 'text-center',
    inner: 'span',
    dynamic: true,
    innerPropsGenerator: () => ({ class: 'pit digit' }),
  },
  {
    field: 'spreads',
    header: 'modal.autoTradeTrigger.spread',
    class: 'text-center w-45',
    inner: 'span',
    dynamic: true,
    innerPropsGenerator: () => ({ class: 'pit digit' }),
  },
  {
    field: 'amount',
    header: 'usdtAmount',
    class: 'text-center w-30',
    inner: 'span',
    dynamic: true,
    innerPropsGenerator: () => ({ class: 'pit digit' }),
  },
  {
    field: 'profile_num',
    header: 'profile',
    class: 'text-center',
    inner: 'span',
    dynamic: true,
    innerPropsGenerator: () => ({ class: 'pit digit' }),
  },
  {
    field: 'active',
    header: 'status',
    class: 'text-center w-40',
    inner: markRaw(ActiveStatusPit),
    dynamic: true,
    innerPropsGenerator: (v) => {
      const data = v as AutoTradeTrigger
      return {
        data: data,
        resetting: resettingId.value === data.id,
        onReset: () => resetFailed(data.id),
      }
    },
  },
  // {
  //   field: 'one_shot',
  //   header: 'modal.autoTradeTrigger.oneShot',
  //   class: 'text-center',
  //   inner: Button,
  //   dynamic: true,
  //   innerPropsGenerator: (v) => {
  //     const data = v as AutoTradeTrigger
  //     return {
  //       size: 'small',
  //       icon: data.one_shot ? 'pi pi-check' : 'pi pi-times',
  //       severity: data.one_shot ? 'success' : 'secondary',
  //       variant: 'text',
  //       rounded: true,
  //       loading: togglingOneShotId.value === data.id,
  //       onClick: () => toggleOneShot(data.id),
  //     }
  //   },
  // },
  {
    field: 'actions',
    header: 'actions',
    class: 'text-center',
    inner: TriggerActions,
    dynamic: true,
    innerPropsGenerator: (v) => {
      const data = v as AutoTradeTrigger
      return {
        data,
        togglingId: togglingId.value,
        deletingId: deletingId.value,
        onToggle: toggleActive,
        onRemove: deleteTrigger,
        onEdit: editTrigger,
      }
    },
  },
]

const formattingRules = {
  ex_buy: (v: { name?: string }) => v?.name ? v.name.charAt(0).toUpperCase() + v.name.slice(1) : '',
  ex_sell: (v: { name?: string }) => v?.name ? v.name.charAt(0).toUpperCase() + v.name.slice(1) : '',
  asset: (v: { name?: string }) => v?.name ? v.name.toUpperCase() : '',
  entry_spread: (v: number | null) => {
    if (v === null || v === undefined || !isFinite(v)) return '—'
    return `${v}%`
  },
  exit_spread: (v: number | null) => {
    if (v === null || v === undefined || !isFinite(v)) return '—'
    return `${v}%`
  },
  spreads: (v: number | null, ctx?: Record<string, unknown>) => {
    if (!ctx || ctx.active === false) {
      return '- / -'
    }
    const spread = ctx["spread"] as number | null
    const backSpread = ctx["backspread"] as number | null

    const spreadStr = isNull(spread) ? "-" : spread?.toFixed(2) + "%"
    const backSpreadStr = isNull(backSpread) ? "-" : backSpread?.toFixed(2) + "%"

    return `${spreadStr} / ${backSpreadStr}`
  },
  amount: (v: number) => {
    if (v === null || v === undefined) return '—'
    return `$${Number(v).toFixed(0)}`
  },
  profile_num: (v: number) => `#${v + 1}`,
  // active: (v: boolean, ctx?: Record<string, unknown>) => {
  //   if (!ctx) return t('inactive')
  //   return statusLabel(ctx as unknown as AutoTradeTrigger)
  // },
  market_type: (v: string) => {
    const map: Record<string, string> = {
      'spot-swap': t('spotSwapLabel'),
      'swap-swap': t('swapSwapLabel'),
      'swap': t('swapSwapLabel'),
    }
    return map[v] ?? v
  },
}
</script>

<template>
  <AdaptiveDialog v-model:visible="visible" pt:root="w-full 2xl:w-[1400px]" pt:content="p-0" pt:header="px-4 py-1"
    :header="$t('modal.autoTradeTriggers.title')">

    <!-- Mobile cards -->
    <div class="block xl:hidden m-2">
      <TableCards :values="triggers" :columns="columns" :formattingRules="formattingRules" />
    </div>

    <!-- Desktop table -->
    <div class="hidden xl:block mb-0">
      <SrDataTable size="small" :value="triggers" :columns="columns" :loading="loading"
        :formattingRules="formattingRules" :fakeRowsCount="2">
        <template #empty>
          <div class="text-center">
            {{ $t('modal.autoTradeTriggers.empty') }}
          </div>
        </template>
      </SrDataTable>
    </div>

    <div class="flex justify-end items-center p-4">
      <Button size="small" variant="outlined" icon="pi pi-plus" @click="openCreateModal" severity="success" />
    </div>

    <AutoTradeTriggerCreateModal ref="createModal" @created="loadTriggers" />
    <AutoTradeTriggerEditModal ref="editModal" @updated="loadTriggers" />
  </AdaptiveDialog>
</template>
